import * as React from "react";
import { useRender } from "@base-ui/react/use-render";

import { cn } from "../lib/utils";

function Card({
  className,
  elevated,
  onClick,
  render,
  ref,
  ...props
}: useRender.ComponentProps<"div"> & {
  elevated?: boolean;
}) {
  return useRender({
    defaultTagName: "div",
    render,
    ref,
    props: {
      "data-slot": "card",
      ...props,
      className: cn(
        // Elevation model: in-flow cards are flat — separated by tone + border,
        // not shadow. A shadow means the card floats above the page, so it is
        // opt-in via `elevated` (used for genuine layering). Don't re-add a
        // default shadow here; see GUIDELINES.md §Shadows.
        "text-card-foreground flex flex-col gap-6 rounded-xl border border-border-subtle py-6",
        elevated ? "bg-surface-elevated shadow-sm" : "bg-surface-base",
        onClick && "cursor-pointer transition-colors hover:bg-surface-hover",
        className,
      ),
      onClick,
    },
  });
}

function CardHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        "@container/card-header grid auto-rows-min items-start gap-2 px-6 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6",
        className,
      )}
      {...props}
    />
  );
}

function CardTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-title"
      className={cn("leading-none font-semibold", className)}
      {...props}
    />
  );
}

function CardDescription({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
}

function CardAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        "col-start-2 row-span-2 row-start-1 self-start justify-self-end",
        className,
      )}
      {...props}
    />
  );
}

function CardContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-content"
      className={cn("px-6", className)}
      {...props}
    />
  );
}

function CardFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-footer"
      className={cn("flex items-center px-6 [.border-t]:pt-6", className)}
      {...props}
    />
  );
}

export {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
};
